import { AlertTriangle } from 'lucide-react';
import { useState } from 'react';
import type { RoutingConfigSkuCoverageImpact, RoutingSkuTargetRecord } from '@nadoutong/contracts';
import { useSetRoutingConfigSkuTarget } from '../../api/useTenantRouting.ts';
import { useT } from '../../i18n/index.ts';
import type { SkuOption } from '../../pages/tenantRoutingShared.ts';
import { Button } from '../../ui/index.ts';
import { coerceErrorMessage, useToast } from '../Toast.tsx';

/**
 * Exclusive-coverage repair (#1296, #1327 PR-4), demoted from a standing
 * page section to a conditional banner at the top of the supply panel
 * (#1428).
 *
 * Exclusive routing configs do not inherit the default pool, so a config
 * missing this SKU makes it unavailable to that config's customers. The fix
 * copies an existing default target into every affected config. The section
 * used to render a permanent "nothing to repair" card, which cost a whole
 * panel of vertical space to say nothing — it now renders only when
 * `affected_active_configs` is non-empty.
 */

function ExclusiveCoverageFixRow({
  sku,
  impact,
  copySource,
}: {
  readonly sku: SkuOption;
  readonly impact: RoutingConfigSkuCoverageImpact;
  readonly copySource: RoutingSkuTargetRecord;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const setConfigTarget = useSetRoutingConfigSkuTarget();
  const [error, setError] = useState<string | null>(null);
  const capabilityId = copySource.capability_id;

  function handleFix(): void {
    if (capabilityId === null) return;
    setError(null);
    setConfigTarget.mutate(
      {
        configId: impact.config_id,
        skuId: sku.skuId,
        body: {
          sku_name: copySource.sku_name,
          vendor: copySource.vendor,
          model: copySource.model,
          capability_id: capabilityId,
          upstream_id: copySource.upstream_id,
          account_id: copySource.account_id,
          key_bucket_id: copySource.key_bucket_id,
          upstream_channel_id: copySource.upstream_channel_id,
          weight: copySource.weight,
          retry_order: copySource.retry_order,
          enabled: copySource.enabled,
        },
      },
      {
        onSuccess: () => toast.success(t('tenant_routing.pool.toast_saved')),
        onError: (mutationError) => setError(coerceErrorMessage(mutationError)),
      },
    );
  }

  return (
    <li
      className="flex flex-wrap items-center gap-2"
      data-testid={`sku-pool-exclusive-impact-${impact.config_id}`}
    >
      <span className="min-w-0 flex-1">
        {t('tenant_routing.sku_pools.exclusive_impact_row', {
          name: impact.config_name,
          id: impact.config_id,
          scope: t(impact.scope === 'global'
            ? 'tenant_routing.sku_pools.exclusive_scope_global'
            : 'tenant_routing.sku_pools.exclusive_scope_org'),
          orgs: String(impact.affected_assigned_org_count),
        })}
      </span>
      {capabilityId === null ? (
        <span className="text-paper-low">{t('tenant_routing.sku_pools.exclusive_fix_unpinned')}</span>
      ) : (
        <Button
          size="sm"
          disabled={setConfigTarget.isPending}
          onClick={handleFix}
          data-testid={`sku-pool-exclusive-fix-${impact.config_id}`}
        >
          {t('tenant_routing.sku_pools.exclusive_fix')}
        </Button>
      )}
      {error !== null ? (
        <span className="w-full text-bad" data-testid={`sku-pool-exclusive-error-${impact.config_id}`}>
          {t('tenant_routing.sku_pools.save_failed', { sku: impact.config_name, message: error })}
        </span>
      ) : null}
    </li>
  );
}

/**
 * Renders nothing unless there is an actual gap AND a target to copy from —
 * the repair is a copy, so with an empty pool there is nothing to offer.
 */
export function ExclusiveCoverageAlert({
  sku,
  impacts,
  copySource,
}: {
  readonly sku: SkuOption;
  readonly impacts: readonly RoutingConfigSkuCoverageImpact[];
  readonly copySource: RoutingSkuTargetRecord | undefined;
}): JSX.Element | null {
  const t = useT();
  if (impacts.length === 0 || copySource === undefined) {
    return null;
  }
  return (
    <div
      role="alert"
      className="m-4 rounded-md border border-warn-line bg-warn-dim/50 px-3 py-2.5 text-dense leading-5 text-warn"
      data-testid="sku-pool-exclusive-coverage-banner"
    >
      <span className="flex items-start gap-2">
        <AlertTriangle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
        {t('tenant_routing.sku_pools.exclusive_impact_title', { count: String(impacts.length) })}
      </span>
      <p className="m-0 mt-1.5 text-micro text-paper-low">{t('sku_detail.exclusive.hint')}</p>
      <ul className="m-0 mt-2 flex list-none flex-col gap-1.5 p-0">
        {impacts.map((impact) => (
          <ExclusiveCoverageFixRow
            key={impact.config_id}
            sku={sku}
            impact={impact}
            copySource={copySource}
          />
        ))}
      </ul>
    </div>
  );
}
